import { Calculation } from "@/services/backend/vbs/calculations/types/calculation";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { NavLink, useNavigate, useParams } from "react-router";
import {
  useVbsCalculationDeleteMutation,
  useVbsCalculationShowQuery,
} from "@/services/backend/vbs/calculations/api/calculation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/components/ui/button";
import { Trash2 } from "lucide-react";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { ProcessPageMainTemplate } from "../../../_components/process-page-main-template";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessNumber } from "@/services/backend/numbers/components/process-number";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function CalculationRoute() {
  const { calculationId } = useParams();
  const { data, isLoading, error } = useVbsCalculationShowQuery({
    id: calculationId!,
  });

  const breadcrumbs = data && (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            <NavLink
              to={`/customers/${data.customerId}`}
              aria-disabled={!data.customerId}
            >
              <CustomerName customerId={data.customerId} />
            </NavLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            <NavLink to={`/processes/vbs/${data.processId!}`}>
              <ProcessNumber
                processId={data.processId!}
                className="font-mono"
              />
            </NavLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            <NavLink to={`/processes/vbs/${data.processId!}/calculations`}>
              {t("Kalkulationen")}
            </NavLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>
            <DocumentNumberById documentId={data.id} />
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {data && <CalculationPage calculation={data} />}
    </ProcessPageMainTemplate>
  );
}

function CalculationPage(props: { calculation: Calculation }) {
  const { calculation } = props;

  return (
    <PersistenceContextProvider>
      <DocumentPage>
        <DocumentPageHeader>
          <DocumentPageNavigation>
            <DocumentPageNavigationLink to="treatment">
              {t("Aufnahme")}
            </DocumentPageNavigationLink>
            <DocumentPageNavigationLink to="calculation">
              {t("Kalkulation")}
            </DocumentPageNavigationLink>
            <DocumentPageNavigationLink to="textblocks">
              {t("Bemerkungen")}
            </DocumentPageNavigationLink>
          </DocumentPageNavigation>
          <DocumentPageActions>
            <PersistenceStateUsingContext className="mr-2" />
            <DeleteCalculationDialog calculationId={calculation.id} />
          </DocumentPageActions>
        </DocumentPageHeader>
        <DocumentPageContent>
          <DocumentPageNavigationOutlet />
        </DocumentPageContent>
      </DocumentPage>
    </PersistenceContextProvider>
  );
}

function DeleteCalculationDialog(props: { calculationId: string }) {
  const { calculationId } = props;
  const [deleteCalculation, { isLoading, error, isSuccess, reset }] =
    useVbsCalculationDeleteMutation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onClick = () => {
    if (isLoading) {
      return;
    }
    deleteCalculation({ id: calculationId });
  };

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: t("Kalkulation gelöscht"),
        variant: "success",
      });
      navigate(`..`);
      reset();
    }
  }, [error, isSuccess, navigate, reset, toast]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Trash2 className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Kalkulation wirklich löschen?")}</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onClick}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
